import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import MapField from 'components/field/MapField';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { PROFILE } from 'services/consts';

import { setUserLocalization } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';

/**
 * @description This component is used for map settings modal.
 * @param {Object} props
 * @param {Object} props.mapSettings - map settings object from Localization.js, which is used to set the default values of the map or to update the map settings.
 * @param {number} props.ID - ID of the user.
 */
const MapSettingsModal = NiceModal.create(({ mapSettings, setMapValue, ID }) => {
	const [isLoading, setIsLoading] = useState(false);

	const dispatch = useDispatch();
	const axios = useAxiosPrivate();
	const mapRef = useRef(null);
	const modal = useModal();

	const { showSuccess } = useAlert();

	const schema = yup.object().shape({
		mapSettings: yup.object().shape({
			lat: yup.number().min(-90).max(90).nullable().required(),
			lng: yup.number().min(-180).max(180).nullable().required(),
			zoom: yup.number().nullable(),
			bounds: yup.object().shape({
				ne: yup.object().shape({
					lat: yup.number().min(-90).max(90).nullable(),
					lng: yup.number().min(-180).max(180).nullable()
				}),
				sw: yup.object().shape({
					lat: yup.number().min(-90).max(90).nullable(),
					lng: yup.number().min(-180).max(180).nullable()
				})
			})
		})
	});
	const {
		watch,
		setValue,
		handleSubmit,
		formState: { isDirty }
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			mapSettings
		}
	});

	const onSubmit = async (data) => {
		setIsLoading(true);

		try {
			const map = mapRef.current;
			const bounds = map?.getBounds();
			const { north, east, south, west } = bounds?.toJSON() ?? {
				north: null,
				east: null,
				south: null,
				west: null
			};

			const mapSettings = watch('mapSettings');
			const amendedMapSettings = {
				...mapSettings,
				zoom: mapRef.current.getZoom(),
				bounds: {
					ne: {
						lat: north,
						lng: east
					},
					sw: {
						lat: south,
						lng: west
					}
				}
			};
			const response = await axios.post(PROFILE.UPDATE_LOCALIZATION_SETTINGS, {
				mapSettings: JSON.stringify(amendedMapSettings),
				ID
			});
			if (response.status === 200) {
				showSuccess();
				setMapValue(amendedMapSettings);
				dispatch(setUserLocalization(data));
				modal.remove();
			} else {
				AppLogger.error(response);
				setIsLoading(false);
			}
		} catch (error) {
			AppLogger.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	const mapChangeHandler = ({ lat, lng }) => {
		setValue('mapSettings', { ...watch('mapSettings'), lat, lng }, { shouldValidate: true, shouldDirty: true });
	};

	return (
		<Modal maxWidth="large" fullwidth>
			<ModalTitle>
				<Typography variant="p1" fontWeight="500">
					configureMap
				</Typography>
			</ModalTitle>
			<ModalContent>
				<div style={{ height: '70dvh' }}>
					<MapField ref={mapRef} setValue={mapChangeHandler} value={watch('mapSettings')} />
				</div>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={modal.remove} />
				<Button
					loading={isLoading}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					label={'save'}
				/>
			</ModalActions>
		</Modal>
	);
});

export default MapSettingsModal;
